/**
 * GLB animation-transfer core logic.
 *
 * All of this runs in the browser. We load two GLB files with three.js, then
 * copy the animation clips from the "source" model onto the "target" model,
 * retargeting each track onto the target bone with the same — or equivalent —
 * name.
 *
 * Rigs in the Mixamo family (Mixamo auto-rigged characters, Ready Player Me
 * avatars, and Blender re-exports of either) share bone-frame conventions:
 * every bone's local axes point the same anatomical way, and children sit along
 * local +Y. What differs between them is the rest pose — how far the arms hang,
 * how the clavicles slope, how the neck curves, how the feet turn — and units.
 * So we reconcile bone names (Mixamo prefixes them with `mixamorig`, Ready
 * Player Me does not), carry each bone's world-space rotation *change* from
 * rest across (with the arms first swung to match the source's rest), and
 * rescale translations for the target's units and proportions.
 */
import * as THREE from "three";
import type { GLTF } from "three/addons/loaders/GLTFLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { GLTFExporter } from "three/addons/exporters/GLTFExporter.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";

// No Draco worker (avoids cross-origin worker issues); meshopt is worker-free.
const loader = new GLTFLoader();
loader.setMeshoptDecoder(MeshoptDecoder);

export interface LoadedModel {
  gltf: GLTF;
  fileName: string;
}

export interface BoneMatch {
  /** Number of animated bones present in the target skeleton. */
  hit: number;
  /** Total number of distinct animated bones referenced by the source clips. */
  total: number;
  /**
   * How many of the hits only matched after name reconciliation — e.g. a
   * `mixamorig` prefix stripped — rather than by exact name.
   */
  remapped: number;
}

export interface TransferResult {
  /** Suggested download file name for the baked GLB. */
  fileName: string;
  /** The exported binary GLB payload. */
  buffer: ArrayBuffer;
  /** Number of tracks dropped because their bone was absent in the target. */
  dropped: number;
  /** Number of source clips that made it onto the target. */
  transferred: number;
  /** Number of tracks whose bone was matched by reconciled (not exact) name. */
  remapped: number;
  /** World-space size ratio (target / source) applied to translations. */
  scale: number;
}

/** Parse an in-memory GLB/GLTF payload into a three.js GLTF object. */
export async function parseModel(data: ArrayBuffer, fileName: string): Promise<LoadedModel> {
  const gltf = await loader.parseAsync(data, "");
  return { gltf, fileName };
}

/** Load a GLB/GLTF file from a browser File into a three.js GLTF object. */
export async function loadModel(file: File): Promise<LoadedModel> {
  return parseModel(await file.arrayBuffer(), file.name);
}

// ---------------------------------------------------------------------------
// Bone name reconciliation
// ---------------------------------------------------------------------------

/**
 * Reduce a node name to a comparable key so equivalent bones across rig
 * flavours line up: `mixamorig:Hips` / `mixamorigHips` / `mixamorig1_Hips`
 * (Mixamo) ↔ `Hips` (Ready Player Me) ↔ `Hips_66` (Blender duplicate suffix).
 * three.js already strips `:` from node names on load, hence the optional
 * separator.
 */
export function canonicalBoneName(name: string): string {
  return name
    .replace(/^mixamorig\d*[:_]?/i, "")
    .replace(/_\d+$/, "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();
}

interface Resolved {
  node: THREE.Object3D;
  /** True when the name matched verbatim, false when reconciled. */
  exact: boolean;
}

/** Prefer a skeleton joint over a mesh or empty that shares its name. */
function preferBone(map: Map<string, THREE.Object3D>, key: string, node: THREE.Object3D) {
  const prev = map.get(key);
  if (!prev || ((node as THREE.Bone).isBone && !(prev as THREE.Bone).isBone)) map.set(key, node);
}

/**
 * Build a lookup from a source node name to the scene node that should receive
 * its tracks: exact name first, reconciled name second.
 */
function makeResolver(scene: THREE.Object3D): (name: string) => Resolved | undefined {
  const exact = new Map<string, THREE.Object3D>();
  const canon = new Map<string, THREE.Object3D>();
  scene.traverse((o) => {
    if (!o.name) return;
    preferBone(exact, o.name, o);
    preferBone(canon, canonicalBoneName(o.name), o);
  });
  return (name) => {
    const e = exact.get(name);
    if (e) return { node: e, exact: true };
    const c = canon.get(canonicalBoneName(name));
    return c ? { node: c, exact: false } : undefined;
  };
}

/** The node a track animates, or "" for a track we can't attribute. */
function trackNode(track: THREE.KeyframeTrack): string {
  return THREE.PropertyBinding.parseTrackName(track.name).nodeName ?? "";
}

/** The distinct bone names referenced by a set of animation clips. */
function animatedBones(clips: THREE.AnimationClip[]): Set<string> {
  const wanted = new Set<string>();
  for (const clip of clips) for (const track of clip.tracks) wanted.add(trackNode(track));
  wanted.delete("");
  return wanted;
}

/**
 * Compare the given clips' animated bones against the target skeleton.
 * Used to warn the user before they commit to a transfer.
 */
export function matchBones(clips: THREE.AnimationClip[], target: GLTF): BoneMatch {
  const resolve = makeResolver(target.scene);
  let hit = 0;
  let remapped = 0;
  const wanted = animatedBones(clips);
  wanted.forEach((name) => {
    const r = resolve(name);
    if (!r) return;
    hit++;
    if (!r.exact) remapped++;
  });
  return { hit, total: wanted.size, remapped };
}

// ---------------------------------------------------------------------------
// Retargeting
// ---------------------------------------------------------------------------

/** World-space height of the rig's hips joint at rest, if it has one. */
function hipsHeight(scene: THREE.Object3D): number | null {
  let hips: THREE.Object3D | null = null;
  scene.traverse((o) => {
    if (!hips && canonicalBoneName(o.name) === "hips") hips = o;
  });
  if (!hips) return null;
  const y = (hips as THREE.Object3D).getWorldPosition(new THREE.Vector3()).y;
  return y > 1e-6 ? y : null;
}

/** World-space height of everything in the scene, if it has any geometry. */
function boundsHeight(scene: THREE.Object3D): number | null {
  const box = new THREE.Box3().setFromObject(scene);
  if (box.isEmpty()) return null;
  const h = box.max.y - box.min.y;
  return h > 1e-6 ? h : null;
}

/**
 * How much bigger the target is than the source, in world units. Measured the
 * same way on both rigs — hips height when both have one, else overall bounds —
 * so that translations (root motion, hip bob) scale with the target's legs.
 */
function sizeRatio(source: THREE.Object3D, target: THREE.Object3D): number {
  for (const measure of [hipsHeight, boundsHeight]) {
    const s = measure(source);
    const t = measure(target);
    if (s !== null && t !== null) return t / s;
  }
  return 1;
}

/** A source node paired with the target node that receives its tracks. */
interface BonePair {
  source: THREE.Object3D;
  target: THREE.Object3D;
  exact: boolean;
}

/** Map every node name in a scene to its node (preferring joints). */
function nodesByName(scene: THREE.Object3D): Map<string, THREE.Object3D> {
  const map = new Map<string, THREE.Object3D>();
  scene.traverse((o) => {
    if (o.name) preferBone(map, o.name, o);
  });
  return map;
}

/** Rotation and scale of a node's world matrix, ignoring translation. */
function worldFrame(node: THREE.Object3D) {
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  node.matrixWorld.decompose(new THREE.Vector3(), q, s);
  return { q, s };
}

const IDENTITY_Q = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

function isIdentity(q: THREE.Quaternion): boolean {
  return 1 - Math.abs(q.w) < 1e-9;
}

/** Rest-pose world rotation of every node in a scene (as loaded from file). */
function restRotations(scene: THREE.Object3D): Map<THREE.Object3D, THREE.Quaternion> {
  const map = new Map<THREE.Object3D, THREE.Quaternion>();
  scene.traverse((o) => map.set(o, worldFrame(o).q));
  return map;
}

/**
 * Bones whose rest *direction* is swung to match the source's before motion is
 * applied. Rigs differ in how far the arms hang at rest (T-pose, A-pose, and
 * everything between); that is a pose difference to cancel, not geometry to
 * keep. Descendants — hand, fingers — swing along with them. Everything else
 * (clavicles, spine, neck, legs, feet) keeps the target's own rest angles, so a
 * Ready Player Me avatar's level shoulders, curved neck and straight feet
 * survive a Mixamo character whose rest has sloping clavicles and turned-out
 * feet.
 */
const ALIGNED_BONES = new Set([
  "leftarm",
  "leftforearm",
  "lefthand",
  "rightarm",
  "rightforearm",
  "righthand",
]);

/**
 * The target's rest pose re-posed so that aligned bones point where their
 * source counterparts do. Returns the world rotation of every target node in
 * that virtual rest; non-aligned nodes keep their own rest, rotated along with
 * any aligned ancestor.
 */
function alignedRest(
  target: THREE.Object3D,
  targetRest: Map<THREE.Object3D, THREE.Quaternion>,
  sourceRest: Map<THREE.Object3D, THREE.Quaternion>,
  counterpart: Map<THREE.Object3D, THREE.Object3D>,
): Map<THREE.Object3D, THREE.Quaternion> {
  const out = new Map<THREE.Object3D, THREE.Quaternion>();
  const swing = new Map<THREE.Object3D, THREE.Quaternion>();
  const dT = new THREE.Vector3();
  const dS = new THREE.Vector3();
  // traverse() is parent-first, so a parent's swing is ready before its children.
  target.traverse((node) => {
    const inherited = (node.parent && swing.get(node.parent)) ?? IDENTITY_Q;
    let m = inherited;
    const src = counterpart.get(node);
    const sRest = src && sourceRest.get(src);
    if (sRest && ALIGNED_BONES.has(canonicalBoneName(node.name))) {
      dT.copy(UP).applyQuaternion(targetRest.get(node)!).applyQuaternion(inherited);
      dS.copy(UP).applyQuaternion(sRest);
      m = new THREE.Quaternion().setFromUnitVectors(dT, dS).multiply(inherited);
    }
    swing.set(node, m);
    out.set(node, m.clone().multiply(targetRest.get(node)!));
  });
  return out;
}

/**
 * Re-express a position track from the source bone's parent space into the
 * target bone's parent space. We keep the target's own rest offset (its bone
 * lengths and proportions) and add on the *change* from the source's rest,
 * scaled to size. For the root-most animated bone the source and target parent
 * frames are static — Mixamo rigs often carry a 0.01-scale armature, Blender
 * exports may rotate it — so we convert through both frames in full. Deeper
 * bones move in an animated parent frame that matches across rigs, so only a
 * uniform scale applies.
 */
function convertPosition(
  track: THREE.KeyframeTrack,
  pair: BonePair,
  topLevel: boolean,
  ratio: number,
) {
  const sParent = pair.source.parent;
  const tParent = pair.target.parent;
  const sFrame = sParent ? worldFrame(sParent) : { q: IDENTITY_Q, s: new THREE.Vector3(1, 1, 1) };
  const tFrame = tParent ? worldFrame(tParent) : { q: IDENTITY_Q, s: new THREE.Vector3(1, 1, 1) };

  const k = new THREE.Matrix3();
  if (topLevel) {
    // K = Mt⁻¹ · ratio · Ms  (linear parts of the parents' world matrices)
    const ms = new THREE.Matrix4().compose(new THREE.Vector3(), sFrame.q, sFrame.s);
    const mt = new THREE.Matrix4().compose(new THREE.Vector3(), tFrame.q, tFrame.s).invert();
    k.setFromMatrix4(mt.multiply(ms)).multiplyScalar(ratio);
  } else {
    const sScale = (sFrame.s.x + sFrame.s.y + sFrame.s.z) / 3;
    const tScale = (tFrame.s.x + tFrame.s.y + tFrame.s.z) / 3;
    k.identity().multiplyScalar((ratio * sScale) / tScale);
  }

  const sRest = pair.source.position;
  const tRest = pair.target.position;
  const p = new THREE.Vector3();
  const v = track.values;
  for (let i = 0; i + 2 < v.length; i += 3) {
    p.fromArray(v, i).sub(sRest).applyMatrix3(k).add(tRest);
    p.toArray(v, i);
  }
}

/**
 * Carry the source bone's world-space rotation *change* from its rest over to
 * the target bone, on top of the target's own (aligned) rest. With S and T the
 * rest world rotations of the source and target bones and their parents, the
 * target's local rotation is
 *
 *     q'(t) = T_parent⁻¹ · S_parent · q(t) · S⁻¹ · T
 *
 * which is a constant pre- and post-multiply of the source's local track — no
 * resampling. At the source's rest pose the target sits in its own rest pose;
 * as the source moves, each target bone turns by the same world-space amount.
 * A bone whose parent is missing on the target degrades gracefully: the
 * missing bone's rest offset is kept and only its motion is lost.
 */
function convertRotation(
  track: THREE.KeyframeTrack,
  pair: BonePair,
  sourceRest: Map<THREE.Object3D, THREE.Quaternion>,
  targetRest: Map<THREE.Object3D, THREE.Quaternion>,
) {
  const sq = sourceRest.get(pair.source)!;
  const tq = targetRest.get(pair.target)!;
  const spq = (pair.source.parent && sourceRest.get(pair.source.parent)) ?? IDENTITY_Q;
  const tpq = (pair.target.parent && targetRest.get(pair.target.parent)) ?? IDENTITY_Q;

  const pre = tpq.clone().invert().multiply(spq); // T_parent⁻¹ · S_parent
  const post = sq.clone().invert().multiply(tq); // S⁻¹ · T
  if (isIdentity(pre) && isIdentity(post)) return; // same rig — keep bit-exact

  const q = new THREE.Quaternion();
  const v = track.values;
  for (let i = 0; i + 3 < v.length; i += 4) {
    q.fromArray(v, i).premultiply(pre).multiply(post);
    q.toArray(v, i);
  }
}

/** True when none of the node's ancestors are animated (and matched) in this clip. */
function isTopLevel(node: THREE.Object3D, pairs: Map<string, BonePair>): boolean {
  for (let p = node.parent; p; p = p.parent) if (pairs.has(p.name)) return false;
  return true;
}

/** Child joint translations define anatomy, even if a clip omits its parent. */
function hasBoneAncestor(node: THREE.Object3D): boolean {
  for (let p = node.parent; p; p = p.parent) if ((p as THREE.Bone).isBone) return true;
  return false;
}

export interface RetargetResult {
  clips: THREE.AnimationClip[];
  dropped: number;
  remapped: number;
  scale: number;
}

/**
 * Rebuild the source clips against the target skeleton: tracks are renamed to
 * the matching target node, rotations are carried over as world-space changes
 * from rest, translations are re-expressed for the target's frame and size,
 * and tracks for bones the target lacks are dropped. Tracks are cloned, so the
 * source model's clips are left untouched.
 *
 * Both files' node transforms are taken as their rest poses — true for Mixamo,
 * Ready Player Me and Blender exports.
 */
export function retargetClips(
  source: GLTF,
  target: GLTF,
  sourceClips: THREE.AnimationClip[],
): RetargetResult {
  source.scene.updateMatrixWorld(true);
  target.scene.updateMatrixWorld(true);

  const resolve = makeResolver(target.scene);
  const sourceNodes = nodesByName(source.scene);
  const scale = sizeRatio(source.scene, target.scene);

  // Which source node each target node stands in for (rig-level, not per clip).
  const counterpart = new Map<THREE.Object3D, THREE.Object3D>();
  for (const s of sourceNodes.values()) {
    const r = resolve(s.name);
    if (!r) continue;
    const prev = counterpart.get(r.node);
    if (!prev || ((s as THREE.Bone).isBone && !(prev as THREE.Bone).isBone)) {
      counterpart.set(r.node, s);
    }
  }
  const sourceRest = restRotations(source.scene);
  const targetRest = alignedRest(target.scene, restRotations(target.scene), sourceRest, counterpart);

  let dropped = 0;
  let remapped = 0;
  const clips: THREE.AnimationClip[] = [];

  for (const clip of sourceClips) {
    // Every node this clip animates that has a counterpart on the target.
    const pairs = new Map<string, BonePair>();
    for (const track of clip.tracks) {
      const name = trackNode(track);
      if (!name || pairs.has(name)) continue;
      const s = sourceNodes.get(name);
      const t = resolve(name);
      if (s && t) pairs.set(name, { source: s, target: t.node, exact: t.exact });
    }

    const tracks: THREE.KeyframeTrack[] = [];
    for (const track of clip.tracks) {
      const parsed = THREE.PropertyBinding.parseTrackName(track.name);
      const name = parsed.nodeName ?? "";
      const pair = pairs.get(name);
      if (!pair) {
        dropped++;
        continue;
      }
      if (!pair.exact) remapped++;

      const outName = track.name.replace(name, pair.target.name);
      const isBone = (pair.target as THREE.Bone).isBone;
      // A donor's translations and scales can reshape the recipient's skeleton.
      // Keep explicit constant tracks so blending with existing clips also
      // restores the target's proportions. Only the skeletal root may translate.
      if (isBone && (parsed.propertyName === "scale" ||
          (parsed.propertyName === "position" && hasBoneAncestor(pair.target)))) {
        const rest = parsed.propertyName === "scale" ? pair.target.scale : pair.target.position;
        tracks.push(new THREE.VectorKeyframeTrack(outName, [0], rest.toArray()));
        continue;
      }

      const out = track.clone();
      out.name = outName;
      if (parsed.propertyName === "position") {
        convertPosition(out, pair, isTopLevel(pair.source, pairs), scale);
      } else if (parsed.propertyName === "quaternion") {
        convertRotation(out, pair, sourceRest, targetRest);
      }
      tracks.push(out);
    }

    if (tracks.length) {
      clips.push(new THREE.AnimationClip(clip.name, clip.duration, tracks, clip.blendMode));
    }
  }
  return { clips, dropped, remapped, scale };
}

// ---------------------------------------------------------------------------
// Grounding
// ---------------------------------------------------------------------------

/** The topmost animated bone with a position track — the clip's "root motion". */
function rootPositionTrack(
  target: GLTF,
  clip: THREE.AnimationClip,
): { track: THREE.KeyframeTrack; node: THREE.Object3D } | null {
  const byName = nodesByName(target.scene);

  let best: { track: THREE.KeyframeTrack; node: THREE.Object3D } | null = null;
  let bestDepth = Infinity;
  for (const track of clip.tracks) {
    const parsed = THREE.PropertyBinding.parseTrackName(track.name);
    if (parsed.propertyName !== "position") continue;
    const node = byName.get(parsed.nodeName ?? "");
    if (!node) continue;
    // Never ground by moving a child joint: that changes the target's anatomy.
    if ((node as THREE.Bone).isBone && hasBoneAncestor(node)) continue;
    let depth = 0;
    for (let p: THREE.Object3D | null = node.parent; p; p = p.parent) depth++;
    if (depth < bestDepth) {
      bestDepth = depth;
      best = { track, node };
    }
  }
  return best;
}

/** Snapshot every node's local transform so sampling can be undone. */
function snapshotTransforms(scene: THREE.Object3D) {
  const snap: Array<[THREE.Object3D, THREE.Vector3, THREE.Quaternion, THREE.Vector3]> = [];
  scene.traverse((o) =>
    snap.push([o, o.position.clone(), o.quaternion.clone(), o.scale.clone()]),
  );
  return () => {
    for (const [o, p, q, s] of snap) {
      o.position.copy(p);
      o.quaternion.copy(q);
      o.scale.copy(s);
    }
    scene.updateMatrixWorld(true);
  };
}

/**
 * Re-ground the target so its feet rest on y=0 while each clip plays.
 *
 * Even after retargeting, a clip's foot placement was authored for the source
 * model's leg and foot geometry. For each clip we pose the target across the
 * whole timeline, find how far the lowest skinned vertex sits from the floor,
 * and shift that clip's root position track by the same amount so the lowest
 * point of the motion just kisses the ground.
 */
export function groundClips(target: GLTF, clips: THREE.AnimationClip[]): void {
  const meshes: THREE.SkinnedMesh[] = [];
  target.scene.traverse((o) => {
    if ((o as THREE.SkinnedMesh).isSkinnedMesh) meshes.push(o as THREE.SkinnedMesh);
  });
  if (!meshes.length) return;

  const restore = snapshotTransforms(target.scene);
  const mixer = new THREE.AnimationMixer(target.scene);
  const SAMPLES = 32;
  const v = new THREE.Vector3();
  const worldToLocal = new THREE.Matrix3();

  for (const clip of clips) {
    const root = rootPositionTrack(target, clip);
    if (!root) continue;

    const action = mixer.clipAction(clip);
    action.reset().play();

    let minY = Infinity;
    const dur = clip.duration || 0;
    for (let i = 0; i < SAMPLES; i++) {
      const t = SAMPLES > 1 ? (dur * i) / (SAMPLES - 1) : 0;
      mixer.setTime(t);
      target.scene.updateMatrixWorld(true);
      for (const mesh of meshes) {
        const pos = mesh.geometry.attributes.position;
        // Stride to keep the cost bounded on dense meshes (sub-mm accuracy is plenty).
        const stride = Math.max(1, Math.floor(pos.count / 4000));
        for (let vi = 0; vi < pos.count; vi += stride) {
          v.fromBufferAttribute(pos, vi);
          mesh.applyBoneTransform(vi, v);
          mesh.localToWorld(v);
          if (v.y < minY) minY = v.y;
        }
      }
    }
    action.stop();

    if (Number.isFinite(minY) && Math.abs(minY) > 1e-6) {
      // We want to lift the whole model by `-minY` in *world* space so the
      // deepest foot rests on y=0. The root track lives in its parent's local
      // space, which may be scaled or rotated — so convert the world-space
      // displacement into that local frame before nudging the keyframes.
      const parent = root.node.parent ?? target.scene;
      worldToLocal.setFromMatrix4(parent.matrixWorld).invert();
      const delta = v.set(0, -minY, 0).applyMatrix3(worldToLocal);
      const values = root.track.values;
      for (let i = 0; i + 2 < values.length; i += 3) {
        values[i] += delta.x;
        values[i + 1] += delta.y;
        values[i + 2] += delta.z;
      }
    }
    mixer.uncacheClip(clip);
  }

  mixer.stopAllAction();
  restore();
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

/**
 * Bake the selected source clips onto the target scene — alongside whichever of
 * the target's existing clips the user chose to keep — and export a binary GLB.
 * Rejects if source clips were selected but none of their tracks matched.
 */
export function transfer(
  source: GLTF,
  target: GLTF,
  sourceClips: THREE.AnimationClip[],
  keepClips: THREE.AnimationClip[],
  targetFileName: string,
): Promise<TransferResult> {
  const { clips, dropped, remapped, scale } = retargetClips(source, target, sourceClips);
  if (sourceClips.length && !clips.length) {
    return Promise.reject(new Error("No tracks matched the target skeleton."));
  }

  // Re-ground the incoming clips so the target's feet land on the floor. The
  // target's own kept clips were authored for it — leave them.
  groundClips(target, clips);

  const fileName = targetFileName.replace(/\.(glb|gltf)$/i, "") + "_animated.glb";
  const animations = [...keepClips, ...clips];

  return new Promise<TransferResult>((resolve, reject) => {
    new GLTFExporter().parse(
      target.scene,
      (result) => {
        // With binary: true the exporter yields an ArrayBuffer.
        resolve({
          fileName,
          buffer: result as ArrayBuffer,
          dropped,
          transferred: clips.length,
          remapped,
          scale,
        });
      },
      (err) => reject(err instanceof Error ? err : new Error(String(err))),
      { binary: true, animations, onlyVisible: false },
    );
  });
}

/** Trigger a browser download for an exported GLB payload. */
export function downloadGlb(buffer: ArrayBuffer, fileName: string): void {
  const blob = new Blob([buffer], { type: "model/gltf-binary" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/** Human-readable list of clip names in a model. */
export function clipNames(gltf: GLTF): string[] {
  return gltf.animations.map((a) => a.name || "unnamed");
}
